import { getPercentage } from 'Data/payment-plan.js'
import BigNumber from 'bignumber.js'

/**
 * Get the final discount for a payment plan based on payment plan discount,
 * its stack policy, and the max discount rules defined from settings
 * @param {{payor_discounts: number, treatment_discounts: number, payor_share: number, max_discount: number, payment_plan_discount: {type: string, value: number, stack: boolean}}} input
 * @returns {{final_discount: { amount: number, percentage: number }, payment_plan_discount: number }}
 */
export function getFinalDiscountExplained({
  payor_discounts,
  treatment_discounts,
  payor_share,
  max_discount,
  payment_plan_discount,
}) {
  // Total payor discount after applied max discount and stack rules
  let final_discount

  // Calculate case closer payment plan discount
  // based on amount or percentage off the payor share
  let payment_plan_discount_amount = payment_plan_discount
    ? calculateValue({
        total: payor_share,
        value: payment_plan_discount.value,
        type: payment_plan_discount.type,
      })
    : 0

  // If the case closer payment plan does not have a discount
  // we keep what we have in treatment planning area
  if (!payment_plan_discount_amount) {
    final_discount = payor_discounts
  } else {
    // If max discount policy is not defined, we still have
    // to check stack policy to define final payer discounts
    if (max_discount === null) {
      // If case closer payment plan discount is stackable, we add it
      // to existing payer discounts coming from treatment planning area
      if (payment_plan_discount.stack) {
        final_discount = payor_discounts + payment_plan_discount_amount
      } else {
        // Otherwise, we have to choose the biggest between existing
        // payer discounts and case closer payment plan discount
        if (payor_discounts >= payment_plan_discount_amount) {
          final_discount = payor_discounts
        } else {
          final_discount = payment_plan_discount_amount
        }
      }
    } else {
      // Calculate how much room is left within max discount amount
      // after treatment discounts from treatment planning area
      let remaining = max_discount - treatment_discounts

      // If there is no more space after the treatment discounts
      // we keep what we have in treatment planning area
      // since the clinic was in charge of configuring those
      if (remaining <= 0) {
        final_discount = payor_discounts
      } else {
        // If case closer payment plan discount is stackable
        // we add it to existing payor discounts coming from
        // treatment planning area depending on how much room is left
        if (payment_plan_discount.stack) {
          remaining = remaining - payor_discounts

          if (remaining <= 0) {
            final_discount = payor_discounts
          } else {
            let final_payment_plan_discount =
              payment_plan_discount_amount <= remaining
                ? payment_plan_discount_amount
                : remaining

            final_discount = payor_discounts + final_payment_plan_discount
          }
        } else {
          // Otherwise, we have to choose the biggest between existing payer
          // discounts and case closer payment plan discount

          // If payor discounts are bigger,
          // we keep what we have in treatment planning area
          // since the clinic was in charge of configuring those
          if (payor_discounts >= payment_plan_discount_amount) {
            final_discount = payor_discounts
          } else {
            final_discount =
              payment_plan_discount_amount <= remaining
                ? payment_plan_discount_amount
                : remaining
          }
        }
      }
    }
  }

  return {
    final_discount: {
      amount: final_discount,
      percentage: getPercentage(final_discount, payor_share),
    },
    payment_plan_discount: payment_plan_discount_amount,
  }
}

let VALUE_TYPE = /** @type {const} */ ({
  PERCENTAGE: 'percentage',
  AMOUNT: 'amount',
})

function calculateValue({ total, value, type }) {
  return value
    ? type === VALUE_TYPE.PERCENTAGE
      ? BigNumber(value)
          .multipliedBy(total)
          .dividedBy(100)
          .decimalPlaces(2)
          .toNumber()
      : value
    : 0
}
